import { useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import DropdownMenu from '@components/ui/DropdownMenu';
import { usePdp } from '@context/PdpContext';
import {
  FitSizeValueInterface,
  SizeChartInterface,
  SizeFitRiseInterface,
} from '@adapters/bigcommerce/types/sizeChart';
import { BrandProduct } from '@adapters/bigcommerce/types/product';

import styles from './ProductSizeSelector.module.scss';

const ProductMeasure = dynamic(
  () => import('@components/product/ProductMeasure/index'),
  {
    loading: () => null,
  }
);

interface ProductMeasurementInterface {
  measure: SizeChartInterface;
  sizeDetail: SizeFitRiseInterface;
  fitAndRise: FitSizeValueInterface[] | null;
  isBike: boolean;
  brandName?: BrandProduct | null;
}

const ProductSizeSelector = ({
  measure,
  sizeDetail,
  fitAndRise,
  isBike,
  brandName,
}: ProductMeasurementInterface) => {
  const { t } = useTranslation('pdp');
  const sizeSelectorRef = useRef<HTMLDivElement>(null);
  const {
    sizes,
    selectedSizeId,
    setSelectedSizeId,
    addToCartClicked,
    setAddToCartClicked,
    isGiftCard,
    selectedSize,
  } = usePdp();

  useEffect(() => {
    if (addToCartClicked && !selectedSize) {
      const pdpSizeSelectContainer = sizeSelectorRef.current;
      if (pdpSizeSelectContainer) {
        pdpSizeSelectContainer.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
      }
    }
  }, [addToCartClicked, selectedSize]);

  const dropdownOptions = (sizes ?? [])
    .filter((size) => size.isColourSelected)
    .map((size) => ({
      value: size.id,
      name: size.name,
      disabled: size.notAvailableForSelectedColour,
    }));

  if (!sizes || !sizes.length) return null;

  if (sizes.length === 1) {
    return (
      <div className={styles.singleSizeContainer}>
        <p className={styles.singleSizeName}>
          {t('product.size')}{' '}
          <strong data-testid="single-size-name">{sizes[0].name}</strong>
        </p>
        {sizeDetail?.size_chart && (
          <ProductMeasure
            measure={measure}
            sizeDetail={sizeDetail}
            fitAndRise={fitAndRise}
            isBike={isBike}
            brandName={brandName}
          />
        )}
      </div>
    );
  }

  const handleChange = (value: string) => {
    const parsedValue = parseInt(value);
    setSelectedSizeId(parsedValue > 0 ? parsedValue : null);
    if (addToCartClicked) {
      setAddToCartClicked(false);
    }
  };

  const showError = addToCartClicked && !selectedSizeId;

  return (
    <div
      className={styles.container}
      ref={sizeSelectorRef}
      id="pdpSizeSelectContainer"
    >
      <div className={styles.labelContainer}>
        <label className={styles.label} htmlFor="pdpSizeSelect">
          {isGiftCard ? t('product.giftcard.amount') : t('product.size')}
        </label>
        {sizeDetail?.size_chart && (
          <ProductMeasure
            measure={measure}
            sizeDetail={sizeDetail}
            fitAndRise={fitAndRise}
            isBike={isBike}
            brandName={brandName}
          />
        )}
        {showError && (
          <strong className={styles.errorMessage}>
            {t('product.size.error')}
          </strong>
        )}
      </div>
      <DropdownMenu
        id="pdpSizeSelect"
        defaultText={
          isGiftCard
            ? t('product.giftcard.amount.defaultText')
            : t('product.select.size')
        }
        selectedOption={selectedSizeId ?? undefined}
        data={dropdownOptions}
        handleChange={handleChange}
        hasError={showError}
      />
    </div>
  );
};

export default ProductSizeSelector;
